'use client'

import React, { FC, ChangeEvent } from 'react'
import { testIds } from '@/root/shared-consts'
import { FieldErrors, FieldValues, UseFormRegister, UseFormSetValue, UseFormWatch } from 'react-hook-form'
import { useGetTranslations } from '@/root/shared-hooks'
import { GeneralInput } from './GeneralInput'
import CountrySelect from './CountrySelect'
import { CompanyFormFields, VALIDATION_MESSAGES, type FieldConfig } from '@/root/modules/shared/ConfirmPage'

const COMPANY_INPUT_TESTID: Record<string, string> = {
  legalName: testIds.confirm.companyLegalName,
  registrationCode: testIds.confirm.companyRegNumber,
  legalAddress: testIds.confirm.companyLegalAddress,
  zip: testIds.confirm.companyZip,
  city: testIds.confirm.companyCity,
  country: testIds.confirm.companyCountry,
}

interface CompanyFormProps {
  register: UseFormRegister<FieldValues>
  setValue: UseFormSetValue<FieldValues>
  watch: UseFormWatch<FieldValues>
  errors: FieldErrors<FieldValues>
  clearErrors: (name?: string) => void
  fields: FieldConfig[]
  onBlur: () => void
  readOnly?: boolean
}

export const CompanyForm: FC<CompanyFormProps> = ({
  register,
  setValue,
  watch,
  errors,
  clearErrors,
  fields,
  onBlur,
  readOnly,
}) => {
  const { getTranslation } = useGetTranslations()

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { id, name, value } = event.target
    setValue(id, value)
    clearErrors(name)
  }
  const renderField = (field: FieldConfig) => {
    const hasError = Boolean(errors[field.id])
    const errorMessage =
      errors[field.id]?.message === VALIDATION_MESSAGES.INVALID_FORMAT
        ? getTranslation('confirmPageErrorMessage')
        : getTranslation('confirmPageRequiredField')

    // Country select field
    if (field.isCountrySelect) {
      return (
        <span key={field.id} data-testid={COMPANY_INPUT_TESTID[field.id]}>
          <CountrySelect
            id={field.id}
            label={field.label}
            setValue={setValue}
            hasError={hasError}
            errorMessage={errorMessage}
            initialValue={watch(CompanyFormFields.COUNTRY)}
            onBlur={onBlur}
            disabled={readOnly}
          />
        </span>
      )
    }

    // Regular input field
    return (
      <GeneralInput
        key={field.id}
        label={field.label}
        id={field.id}
        testId={COMPANY_INPUT_TESTID[field.id]}
        type={field.type}
        placeholder={field.placeholder}
        onChange={handleChange}
        hasError={hasError}
        errorMessage={errorMessage}
        validationPattern={field.validationPattern!}
        register={register}
        onBlur={onBlur}
        readOnly={readOnly}
        showRequired
      />
    )
  }

  // Group fields by rows for layout (address+zip, city+country)
  const renderFieldsWithLayout = () => {
    const result: React.ReactNode[] = []

    for (let i = 0; i < fields.length; i++) {
      const field = fields[i]
      const nextField = fields[i + 1]

      // Check if current field and next field should be in the same row
      if (field.widthClass && nextField?.widthClass) {
        result.push(
          <div key={`row-${field.id}`} className="flex w-full gap-2.5">
            <div className={field.widthClass}>{renderField(field)}</div>
            <div className={nextField.widthClass}>{renderField(nextField)}</div>
          </div>
        )
        i++ // Skip next field since we already rendered it
      } else {
        result.push(renderField(field))
      }
    }

    return result
  }

  return (
    <div className="border-neutral-normal-lighter flex flex-col gap-5 border-t pt-5">
      <h3 className="text-lg font-medium">{getTranslation('confirmPageCompanyDetails')}</h3>
      {renderFieldsWithLayout()}
    </div>
  )
}

export default CompanyForm
